'use client'

import { cn } from '@/lib/utils'
import { useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

/**
 * Easing functions for animation
 */
const easingFunctions = {
  linear: (t: number) => t,
  easeInQuad: (t: number) => t * t,
  easeOutQuad: (t: number) => t * (2 - t),
  easeInOutQuad: (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  easeInCubic: (t: number) => t * t * t,
  easeOutCubic: (t: number) => --t * t * t + 1,
  easeInOutCubic: (t: number) => (t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1),
  easeOutExpo: (t: number) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)),
  easeInExpo: (t: number) => (t === 0 ? 0 : Math.pow(2, 10 * (t - 1))),
  easeInOutExpo: (t: number) => (t === 0 ? 0 : t === 1 ? 1 : t < 0.5 ? Math.pow(2, 10 * (2 * t - 1)) / 2 : (2 - Math.pow(2, -10 * (2 * t - 1))) / 2),
  bounce: (t: number) => {
    const n1 = 7.5625
    const d1 = 2.75
    if (t < 1 / d1) {
      return n1 * t * t
    } else if (t < 2 / d1) {
      return n1 * (t -= 1.5 / d1) * t + 0.75
    } else if (t < 2.5 / d1) {
      return n1 * (t -= 2.25 / d1) * t + 0.9375
    } else {
      return n1 * (t -= 2.625 / d1) * t + 0.984375
    }
  },
}

type EasingFunction = keyof typeof easingFunctions

export interface AnimatedCounterProps {
  /**
   * The final value to count up to (can be a number or string like "100+")
   */
  value: number | string
  /**
   * Duration of the animation in milliseconds
   * @default 2000
   */
  duration?: number
  /**
   * Delay before starting the animation in milliseconds
   * @default 0
   */
  delay?: number
  /**
   * Format the displayed value (e.g., add commas, prefix, suffix)
   * @default (value) => value.toString()
   */
  formatter?: (value: number) => string
  /**
   * CSS class name for the counter
   */
  className?: string
  /**
   * Easing function to use for the animation
   * @default "easeOutExpo"
   */
  easing?: EasingFunction
  /**
   * Intersection observer threshold (0-1)
   * @default 0.1
   */
  threshold?: number
  /**
   * Whether to start from zero or another value
   * @default 0
   */
  startValue?: number
  /**
   * Whether to use decimal precision in the animation
   * @default false
   */
  useDecimals?: boolean
  /**
   * Number of decimal places to show when useDecimals is true
   * @default 2
   */
  decimals?: number
}

/**
 * AnimatedCounter - A professional counter animation component that counts up to a target value
 * when scrolled into view, with customizable easing, formatting, and animation options.
 */
export default function AnimatedCounter({
  value,
  duration = 2000,
  delay = 0,
  formatter = value => value.toString(),
  className = '',
  easing = 'easeOutExpo',
  threshold = 0.1,
  startValue = 0,
  useDecimals = false,
  decimals = 2,
}: AnimatedCounterProps) {
  const [displayValue, setDisplayValue] = useState(startValue)
  const counterRef = useRef<HTMLSpanElement>(null)
  const startTimeRef = useRef<number | null>(null)
  const frameRef = useRef<number | null>(null)
  const delayTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const { ref: inViewRef, inView } = useInView({
    threshold,
    triggerOnce: true,
  })

  // Parse the value to handle string values like "100+" or "24/7"
  const parseValue = (val: number | string): { numericValue: number; suffix: string } => {
    if (typeof val === 'number') return { numericValue: val, suffix: '' }

    // Extract numeric part and any suffix
    const match = val.toString().match(/^(\d+(?:\.\d+)?)(.*)$/)
    if (match) {
      return {
        numericValue: parseFloat(match[1]) || 0,
        suffix: match[2] || '',
      }
    }
    return { numericValue: 0, suffix: val.toString() }
  }

  const { numericValue, suffix } = parseValue(value)

  useEffect(() => {
    // Only start animation when in view
    if (!inView) return

    // Reset animation state
    setDisplayValue(startValue)
    startTimeRef.current = null

    // Clear any existing animations
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }

    if (delayTimeoutRef.current) {
      clearTimeout(delayTimeoutRef.current)
      delayTimeoutRef.current = null
    }

    // Skip animation for values equal to startValue
    if (numericValue === startValue) {
      setDisplayValue(startValue)
      return
    }

    // Apply delay if specified
    if (delay > 0) {
      delayTimeoutRef.current = setTimeout(startAnimation, delay)
      return
    }

    startAnimation()

    function startAnimation() {
      const easingFunction = easingFunctions[easing]

      const animate = (timestamp: number) => {
        if (!startTimeRef.current) startTimeRef.current = timestamp
        const elapsed = timestamp - startTimeRef.current
        const progress = Math.min(elapsed / duration, 1)

        // Apply the selected easing function
        const easedProgress = easingFunction(progress)

        // Calculate current value based on progress
        const range = numericValue - startValue
        const currentValue = startValue + range * easedProgress

        // Set the display value with or without decimals
        if (useDecimals) {
          setDisplayValue(parseFloat(currentValue.toFixed(decimals)))
        } else {
          setDisplayValue(Math.floor(currentValue))
        }

        // Continue animation if not complete
        if (progress < 1) {
          frameRef.current = requestAnimationFrame(animate)
        } else {
          setDisplayValue(useDecimals ? parseFloat(numericValue.toFixed(decimals)) : numericValue)
        }
      }

      frameRef.current = requestAnimationFrame(animate)
    }

    // Cleanup animation on unmount or when dependencies change
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current)
      }
      if (delayTimeoutRef.current) {
        clearTimeout(delayTimeoutRef.current)
      }
    }
  }, [numericValue, duration, delay, inView, easing, startValue, useDecimals, decimals])

  return (
    <span
      ref={el => {
        // Combine both refs
        counterRef.current = el
        inViewRef(el)
      }}
      className={cn(className)}
      aria-live='polite'
      aria-atomic='true'
    >
      {formatter(displayValue)}
      {suffix}
    </span>
  )
}
